// frontend/src/api/transport.js
// Base HTTP transport utilities extracted from DatabaseAPI.js
// Phase 1 Platform Extraction — Step 15B

import { handleApiAuthError } from '../auth/authUtils';

// Use environment variables to determine API base URL
const isDevelopment = process.env.NODE_ENV === 'development';
const useReverseProxy = process.env.REACT_APP_USE_REVERSE_PROXY === 'true';

// In development without reverse proxy: use direct URL
// Otherwise: use relative URL for reverse proxy
const isFakeLocalProd = false; // Only set when faking prod locally
export const API_BASE = isFakeLocalProd || (isDevelopment && !useReverseProxy)
  ? `http://${window.location.hostname}:${process.env.REACT_APP_BACKEND_PORT}/api`
  : `/api`;

// The backend origin (scheme + host + port) used for absolute URL construction.
// In dev without a reverse proxy the React dev server is on a different port
// from Django, so relative paths like /api/v2/player-variants/media/<uuid>/
// resolve to the wrong origin when used as <img src>. Use this helper wherever
// a backend-relative URL must be embedded in an <img> or <a> tag.
//
// In production (or dev with reverse proxy) the frontend and backend share the
// same origin, so relative paths work fine and this returns the path unchanged.
const BACKEND_ORIGIN = isFakeLocalProd || (isDevelopment && !useReverseProxy)
  ? `http://${window.location.hostname}:${process.env.REACT_APP_BACKEND_PORT}`
  : '';

export function resolveMediaUrl(relativeUrl) {
  if (!relativeUrl) return relativeUrl;
  // Already absolute (e.g. https://...) — return as-is.
  if (/^https?:\/\//.test(relativeUrl)) return relativeUrl;
  return `${BACKEND_ORIGIN}${relativeUrl}`;
}

export const ENDPOINTS = {
    // Keep v1 endpoints for now during transition
    "TEAMS": "teams",
    "WEEK": "week",
    "MATCHUP": "matchups",
    "WEEKSTATS": "weekStats",
    "CHARACTERSHEET": "characterSheet",

    // Add v2 endpoints
    "TEAM_STATS": "v2/teams",
    "PLAYER_STATS": "v2/players",
    "LEAGUE_STATS": "v2/leagues",
    "ROSTER": "v2/teams",

    // Authentication endpoints
    "AUTH": {
        "VALIDATE_BETA_KEY": "v2/auth/validate-beta-key",
        "REGISTER": "v2/auth/register",
        "LOGIN": "v2/auth/login",
        "LOGOUT": "v2/auth/logout",
        "CURRENT_USER": "v2/auth/me",
        "CLERK_STATUS": "v2/auth/clerk/status",
        "CLERK_BRIDGE": "v2/auth/clerk",
        "USER_TEAMS": "v2/auth/teams",
        "TEAM_NAMING_DEFAULTS": "v2/auth/team-naming-defaults"
    },

    // Team management endpoints
    "TEAMS": {
        "NAMING": "v2/teams"
    },

    // League management endpoints
    "LEAGUES": {
        "AVAILABLE": "v2/leagues/available",
        "JOIN": "v2/leagues/join",
        "CREATE_PRIVATE": "v2/leagues/create-private"
    }
};

export const CALLDATA_INDEX = {
    "ENDPOINT": 0,
    "PARAM": 1
};

// Utility function to get CSRF token from cookies
export function getCsrfToken() {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'csrftoken') {
            return value;
        }
    }
    return null;
}

// Utility function to get CSRF headers.
//
// MANDATORY for every mutating helper (POST/PUT/PATCH/DELETE) in this codebase.
// DRF is configured with SessionAuthentication as the DEFAULT authentication
// class (backend/football_admin/settings.py REST_FRAMEWORK), and
// SessionAuthentication.enforce_csrf() rejects an authenticated mutating
// request with 403 when the CSRF token is absent. Pair it ALWAYS with
// `credentials: 'include'` — without the cookie the request is anonymous, and
// without the header an authenticated request is refused.
//
// Returns {} (not a null-valued header) when no cookie is present, so spreading
// it never injects a bogus `X-CSRFToken: null`.
export function getCsrfHeaders() {
    const token = getCsrfToken();
    return token ? { 'X-CSRFToken': token } : {};
}

// Legacy transport functions (kept for backward compat)
//
// NOTE: dbGet/dbPut are DEAD CODE — they target a hardcoded
// http://localhost:5000 origin that no longer exists in any environment, and a
// repository-wide search finds no callers. They are intentionally left
// unmodified (adding CSRF/credentials would imply they are usable) and should
// be deleted outright once someone confirms no external consumer imports them.
export function dbGet(endpointData, callback) {
    let url = 'http://localhost:5000/' + endpointData[CALLDATA_INDEX.ENDPOINT];

    if (endpointData[CALLDATA_INDEX.PARAM]) {
        url += "?id=" + endpointData[CALLDATA_INDEX.PARAM];
    }

    fetch(url)
    .then(response => response.json())
    .then(data => callback(data))
    .catch(error => console.error('Error fetching data:', error));
}

export function dbPut(endpointData) {
    const url = 'http://localhost:5000/' + endpointData[CALLDATA_INDEX.ENDPOINT];

    fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(endpointData[CALLDATA_INDEX.PARAM]),
      })
        .then(response => response.json())
        .catch(error => console.error('Error:', error));
}
